import moment from "moment";
import Section from "../../parts/Section";
import DiscreetMessage from "../../parts/DiscreetMessage";
import { useState } from "react";
import Api from "../../Api";
import classNames from "classnames";
import { intToBrl } from "../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RentContract, RentContractPlan, RentReceipt } from "../../types";

const FinanceContractHistory = ({
  contract,
  plan,
  reload,
}: {
  contract: RentContract;
  plan: RentContractPlan;
  reload: () => Promise<void>;
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Section>
      <div className="p-4 flex flex-col gap-4 pb-32">
        {contract.suspended && (
          <div className="p-2 text-center uppercase text-sm text-gray-700 font-light bg-gray-50 rounded">
            Contrato suspenso
          </div>
        )}

        {Object.keys(plan).length === 0 && !contract.suspended && (
          <DiscreetMessage text="Nenhum resultado" />
        )}

        {Object.keys(plan).map((ref) => (
          <div key={ref} className="flex flex-col gap-4">
            {plan[ref].filter((rec) => !rec.canceled).length === 0 && (
              <>
                {!contract.suspended && (
                  <PendingReceipt
                    key={ref}
                    contractUuid={contract.uuid}
                    reference={ref}
                    amount={contract.amount}
                    emails={contract.emails}
                    reload={reload}
                  />
                )}

                {contract.suspended && (
                  <div className="flex border border-dashed rounded-lg items-center">
                    <div className="py-4 px-2 border-r border-dashed font-mono font-bold text-lg text-gray-400">
                      {moment(ref, "YYYY/MM").format("MM/YY")}
                    </div>
                    <div className="p-4 font-semibold text-gray-400">
                      Nenhuma nota emitida
                    </div>
                  </div>
                )}
              </>
            )}
            {plan[ref].map((receipt) => (
              <Receipt
                key={receipt.uuid}
                receipt={receipt}
                emails={contract.emails}
                contractSuspended={contract.suspended}
                warning={plan[ref].filter((rec) => !rec.canceled).length > 1}
                expanded={expanded === receipt.uuid}
                setExpanded={(expanded: boolean) =>
                  expanded ? setExpanded(receipt.uuid) : setExpanded(null)
                }
                reload={reload}
              />
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
};

export default FinanceContractHistory;

const Receipt = ({
  receipt,
  emails,
  contractSuspended,
  warning,
  expanded,
  setExpanded,
  reload,
}: {
  receipt: RentReceipt;
  emails: string[];
  contractSuspended: boolean;
  warning: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  reload: () => Promise<void>;
}) => {
  const [saving, setSaving] = useState(false);

  const view = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`/externo/nota-fatura/${receipt.externalHash}`);
  };

  const resend = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const msg = ["Reenviar nota?", "Destinatários:"]
      .concat(emails.map((email) => `•  ${email}`))
      .join("\n");
    if (window.confirm(msg)) {
      setSaving(true);
      await Api.financeEmailRent(receipt.uuid);
      await reload();
      setExpanded(false);
      setSaving(false);
    }
  };

  const cancel = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const msg = `Confirmar cancelamento da nota?`;
    if (window.confirm(msg)) {
      setSaving(true);
      await Api.financeCancelRent(receipt.uuid);
      await reload();
      setExpanded(false);
      setSaving(false);
    }
  };

  return (
    <div
      className={classNames("border rounded-lg shadow-sm flex", {
        "border-red-600": receipt.canceled,
        "border-amber-400 text-amber-500": warning && !receipt.canceled,
        "hover:bg-slate-50 cursor-pointer": !expanded,
      })}
      onClick={() => setExpanded(!expanded)}
    >
      <div
        className={classNames(
          "border-r uppercase font-mono font-bold p-2 py-4 flex items-center text-lg text-center",
          {
            "border-red-600 text-red-700": receipt.canceled,
            "border-amber-400 text-amber-500": warning && !receipt.canceled,
          }
        )}
      >
        {moment(receipt.installment, "YYYY/MM").format("MM/YY")}
      </div>

      <div className="flex flex-col flex-1 truncate">
        <div className="flex flex-1 flex-wrap items-center gap-2 px-2 py-4 font-semibold">
          <Badge>{intToBrl(receipt.amount)}</Badge>

          <Badge>
            <span className="flex gap-1 items-center">
              <FontAwesomeIcon icon="calendar" className="text-gray-500" />
              {moment(receipt.date).format("DD/MM/YYYY")}
            </span>
          </Badge>

          <Badge>
            <div className="flex gap-1 items-center">
              <FontAwesomeIcon icon="envelope" className="text-gray-500" />
              {receipt.imported && <span>?</span>}
              {!receipt.imported && <span>{receipt.sends.length}</span>}
            </div>
          </Badge>

          {receipt.canceled && (
            <div className="bg-red-100 text-red-700 py-0.5 px-2 text-xs rounded-full">
              Cancelada
            </div>
          )}

          {receipt.note && (
            <Badge>
              <span
                title={receipt.note}
                className="font-light tracking-tight flex gap-1 items-center overflow-scroll no-scrollbar"
              >
                <FontAwesomeIcon icon="comment" className="text-gray-500" />
                {receipt.note}
              </span>
            </Badge>
          )}
        </div>

        {expanded && (
          <div className="flex border-t">
            <div className="flex">
              {!contractSuspended && !receipt.canceled && (
                <button
                  className="p-2 text-xs font-bold border-r text-red-700  hover:bg-red-50"
                  onClick={cancel}
                  disabled={saving}
                >
                  Cancelar
                </button>
              )}

              {!contractSuspended && !receipt.canceled && (
                <button
                  className="p-2 text-xs font-bold border-r hover:bg-slate-50"
                  onClick={resend}
                  disabled={saving}
                >
                  Reenviar
                </button>
              )}

              <button
                className="p-2 text-xs font-bold border-r hover:bg-slate-50"
                onClick={view}
              >
                Visualizar
              </button>
            </div>

            <button
              className="px-2 text-xs rounded-br-lg hover:bg-slate-50 flex-1 text-right"
              onClick={(e) => [e.stopPropagation(), setExpanded(false)]}
            >
              <FontAwesomeIcon icon="chevron-up" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const PendingReceipt = ({
  contractUuid,
  reference,
  amount,
  emails,
  reload,
}: {
  contractUuid: string;
  reference: string;
  amount: number;
  emails: string[];
  reload: () => void;
}) => {
  const [saving, setSaving] = useState(false);

  const issue = async () => {
    if (saving) return;

    const msg = ["Emitir nota e enviar por email?", "Destinatários:"]
      .concat(emails.map((email) => `•  ${email}`))
      .join("\n");
    if (window.confirm(msg)) {
      setSaving(true);
      const result = await Api.financeIssueRent(contractUuid, reference);
      await Api.financeEmailRent(result.uuid!);
      reload();
    }
  };

  return (
    <div className="border border-green-600 rounded-lg flex">
      <div className="border-r uppercase font-mono font-bold p-2 py-4 flex items-center text-lg bg-green-50 text-green-700 border-green-600 rounded-l-lg">
        {moment(reference, "YYYY/MM").format("MM/YY")}
      </div>

      <div className="p-2 flex flex-wrap items-center gap-2 flex-1">
        <Badge>
          <span className="font-semibold">{intToBrl(amount)}</span>
        </Badge>

        <div className="bg-green-100 text-green-700 py-0.5 px-2 text-xs rounded-full font-bold">
          Pendente
        </div>
      </div>

      <div
        className="border-green-600 border-l p-2 text-sm flex items-center text-green-700 font-bold cursor-pointer hover:bg-green-50 rounded-r-lg"
        onClick={issue}
      >
        Emitir
      </div>
    </div>
  );
};

const Badge = ({ children }: { children: React.ReactNode }) => (
  <div
    className={`text-xs py-0.5 px-2 bg-gray-100 text-gray-800 rounded-full truncate`}
  >
    {children}
  </div>
);
